import { useEffect, useState } from 'react';
import { Button, Dialog, TextField } from '@nyce/admin-kit/ui';
import { ErrorBox } from '../../components/common';
import { stats, type OrganizationDetail } from '../../api/stats';

/** The highest organization id the collector knows + 1 (ids are PMS customer ids; the collector allocates none). */
export async function nextFreeOrganizationId(): Promise<number> {
  let max = 0;
  for (let offset = 0; offset < 20_000; offset += 500) {
    const page = await stats.organizations({ offset, limit: 500 });
    for (const o of page.items) max = Math.max(max, o.customerId);
    if (!page.hasMore) break;
  }
  return max + 1;
}

/** Creates an organization row (`PUT …/monitoring` creates a missing one) with monitoring on. */
export function NewOrganizationDialog({
  open,
  onClose,
  onCreated,
  initialName = '',
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (o: OrganizationDetail) => void;
  initialName?: string;
}) {
  const [id, setId] = useState('');
  const [hint, setHint] = useState<number | null>(null);
  const [name, setName] = useState(initialName);
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(initialName);
    void nextFreeOrganizationId()
      .then((n) => {
        setHint(n);
        setId((cur) => cur || String(n));
      })
      .catch(() => setHint(null));
  }, [open, initialName]);
  const customerId = Number(id);
  const valid = Number.isInteger(customerId) && customerId > 0 && !!name.trim();
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const existing = await stats.organization(customerId).catch(() => null);
      if (existing)
        throw new Error(`Organization ${customerId} exists already (${existing.name ?? 'no name'}).`);
      const o = await stats.updateMonitoring(customerId, {
        name: name.trim(),
        active: true,
        location: location.trim() || null,
      });
      onCreated(o);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New organization"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!valid} onClick={() => void create()}>
            Create
          </Button>
        </>
      }
    >
      <div className="nyk-form">
        <ErrorBox error={error} />
        <TextField
          label="Customer id"
          inputMode="numeric"
          value={id}
          onChange={(e) => setId(e.target.value.replace(/\D/g, ''))}
          hint={
            hint
              ? `The PMS customer id; the next free one is ${hint}.`
              : 'The PMS customer id (the collector allocates none).'
          }
        />
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
      </div>
    </Dialog>
  );
}
